/* eslint-disable @typescript-eslint/no-explicit-any */
import fuzzysearch from 'fuzzysearch'
import { API_ENDPOINTS } from 'routes/constants'
import {
  ScenariosTagsData,
  ScenarioTemplateCompact,
  ScenarioTemplateExtended,
} from './types'

export async function fetchAllScenarios() {
  const response = await fetch(API_ENDPOINTS.SCENARIO_TEMPLATES)

  const { items } = await response.json()

  const scenarios = (items as Array<Record<string, any>>).map(
    mapScenariosFromResponse,
  )

  return sortScenariosByPopularity(scenarios)
}

const mapScenariosFromResponse = (
  scenario: Record<string, any>,
): ScenarioTemplateCompact => {
  return {
    id: scenario.id,
    key: scenario.key,
    name: scenario.name,
    popularity: scenario?.popularity ?? null,
    description: scenario?.description ?? null,
    shortDescription: scenario?.shortDescription ?? null,
    connectors: scenario?.connectors ?? [],
    tags: scenario?.tags ?? [],
  }
}

export async function fetchSingleScenario(scenarioKey: string) {
  const response = await fetch(
    `${API_ENDPOINTS.SCENARIO_TEMPLATES}/by-key/${scenarioKey}`,
  )

  return (await response.json()) as ScenarioTemplateExtended
}

const sortScenariosByPopularity = (scenarios: ScenarioTemplateCompact[]) => {
  return scenarios.sort((a, b) => (b?.popularity ?? 0) - (a?.popularity ?? 0))
}

export const getFilteredScenarios = (
  search: string,
  tag: string,
  scenarios: ScenarioTemplateCompact[],
) => {
  if (search) {
    const normalizedSearch = search.toLowerCase().trim()

    return scenarios.filter(({ name }) =>
      fuzzysearch(normalizedSearch, name.toLowerCase()),
    )
  }

  if (tag) {
    return scenarios.filter(({ tags }) => tags.includes(tag))
  }

  return scenarios
}

export const getSimilarScenariosByTag = (
  tag: string,
  scenarioKey: string,
  scenarios: ScenarioTemplateCompact[],
) => {
  return scenarios.filter(
    ({ tags, key }) => tags.includes(tag) && scenarioKey !== key,
  )
}

const getAllScenariosTags = (scenarios: ScenarioTemplateCompact[]) =>
  scenarios.reduce((allTags, scenario) => {
    allTags.push(...scenario.tags)

    return allTags
  }, [] as string[])

const getUniqueScenariosTags = (tags: string[]) => {
  return tags.filter((tag, idx, tagsArray) => tagsArray.indexOf(tag) === idx)
}

export const getScenariosTagsDataWithCount = (
  scenarios: ScenarioTemplateCompact[],
) => {
  const allTags = getAllScenariosTags(scenarios)
  const uniqueTags = getUniqueScenariosTags(allTags)

  const tagsData: ScenariosTagsData[] = [
    {
      tag: null,
      count: scenarios.length,
    },
  ]

  uniqueTags.map((tagName) => {
    let count: number = 0

    allTags.map((tag) => {
      if (tag === tagName) {
        count += 1
      }
    })
    tagsData.push({ tag: tagName, count })
  })

  return tagsData.sort((a, b) => b.count - a.count)
}

export const isActiveScenariosTag = (
  tag: ScenariosTagsData['tag'],
  selectedTag: string,
) => {
  return tag === selectedTag && tag !== null
}

export const getReadableScenariosTagName = (tag: ScenariosTagsData['tag']) => {
  if (!tag) return 'All'

  const hyphenatedBiDirectional = 'bi-directional'
  const spacedBiDirectional = 'bi directional'
  const lowerCaseApi = ' api'
  const upperCaseApi = ' API'
  const readableTag = tag.replaceAll('-', ' ')

  if (readableTag.includes(spacedBiDirectional)) {
    return readableTag.replace(spacedBiDirectional, hyphenatedBiDirectional)
  }

  if (readableTag.includes(lowerCaseApi)) {
    return readableTag.replace(lowerCaseApi, upperCaseApi)
  }

  return readableTag
}
